import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const AUTH_INVALIDATED_EVENT = 'auctionai:auth-invalidated';

const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};


// Install a global axios interceptor so every API call carries the token.
axios.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// On 401 from the API, drop credentials so the LoginGate is shown.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearStoredAuth();
      try {
        window.dispatchEvent(new Event(AUTH_INVALIDATED_EVENT));
      } catch {
        // no-op
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleInvalidated = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener(AUTH_INVALIDATED_EVENT, handleInvalidated);
    return () => window.removeEventListener(AUTH_INVALIDATED_EVENT, handleInvalidated);
  }, []);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/login', { username, password });
      const { token: t, user: u } = res.data;
      localStorage.setItem(TOKEN_KEY, t);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      setToken(t);
      setUser(u);
      return true;
    } catch (e) {
      const code = e?.response?.data?.error;
      let msg;
      if (code === 'invalid_credentials') {
        msg = 'Invalid username or password';
      } else if (code === 'account_disabled') {
        msg = e?.response?.data?.message
          || 'Your account is awaiting administrator approval.';
      } else {
        msg = 'Login failed';
      }
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(async ({ email, username, password }) => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/auth/signup', { email, username, password });
      // New accounts are created disabled — a superuser must approve them
      // before login is allowed. We deliberately do NOT auto-login here.
      return { ok: true, pendingApproval: true };
    } catch (e) {
      const code = e?.response?.data?.error;
      const msgMap = {
        missing_fields: 'Please fill in email, username and password.',
        invalid_email: 'Please enter a valid email address.',
        invalid_username: 'Username must be 3–150 chars (letters, digits, _.-).',
        weak_password: e?.response?.data?.message || 'Password is too weak.',
        username_taken: 'That username is already taken.',
        email_taken: 'An account with that email already exists.',
        already_exists: 'That username or email is already in use.',
        database_unavailable: 'Service is temporarily unavailable, please try again.',
      };
      setError(msgMap[code] || 'Sign-up failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/forgot-password', { email });
      // Backend now mirrors the admin "issue token" endpoint: when an active
      // account matches it returns the raw one-time token + expiry. Surface
      // both to the caller so the reset form can be auto-filled. When no
      // active account matches the response is just `{ok: true}` — we don't
      // expose that distinction in any error message to avoid enumeration.
      return {
        ok: true,
        token: res?.data?.token || null,
        expiresAt: res?.data?.expires_at || null,
        username: res?.data?.username || null,
      };
    } catch (e) {
      setError('Could not start password reset. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async ({ token: resetToken, password }) => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/auth/reset-password', { token: resetToken, password });
      return { ok: true };
    } catch (e) {
      const code = e?.response?.data?.error;
      const msgMap = {
        missing_fields: 'Please paste your reset token and choose a new password.',
        weak_password: e?.response?.data?.message || 'Password is too weak.',
        invalid_or_expired: 'That reset token is invalid or has expired. Please request a new one.',
        database_unavailable: 'Service is temporarily unavailable, please try again.',
      };
      setError(msgMap[code] || 'Could not reset password. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const changePassword = useCallback(async ({ currentPassword, newPassword }) => {
    setError('');
    try {
      await axios.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return { ok: true };
    } catch (e) {
      const code = e?.response?.data?.error;
      const msgMap = {
        missing_fields: 'Please fill in both fields.',
        invalid_credentials: 'Your current password is incorrect.',
        weak_password: e?.response?.data?.message || 'Password is too weak.',
        same_password: 'New password must differ from the current password.',
        database_unavailable: 'Service is temporarily unavailable, please try again.',
      };
      return { ok: false, error: msgMap[code] || 'Could not change password.' };
    }
  }, []);

  const deleteAccount = useCallback(async ({ password }) => {
    setError('');
    try {
      await axios.delete('/api/auth/account', { data: { password } });
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      setToken(null);
      setUser(null);
      return { ok: true };
    } catch (e) {
      const code = e?.response?.data?.error;
      const msgMap = {
        missing_fields: 'Please enter your password to confirm.',
        invalid_credentials: 'Incorrect password.',
        cannot_delete_superuser: e?.response?.data?.message
          || 'Superuser accounts must be removed by another superuser.',
        database_unavailable: 'Service is temporarily unavailable, please try again.',
      };
      return { ok: false, error: msgMap[code] || 'Could not delete account.' };
    }
  }, []);

  const logout = useCallback(() => {
    const activeToken = token || localStorage.getItem(TOKEN_KEY);
    if (activeToken) {
      axios.post(
        '/api/auth/logout',
        {},
        { headers: { Authorization: `Bearer ${activeToken}` } },
      ).catch(() => {});
    } else {
      axios.post('/api/auth/logout').catch(() => {});
    }

    clearStoredAuth();
    setToken(null);
    setUser(null);
  }, [token]);

  // Validate token on first mount, in case the server-side secret changed
  // or the user was deleted/disabled.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get('/api/auth/me')
      .then((r) => { if (!cancelled) setUser(r.data); })
      .catch(() => { if (!cancelled) logout(); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line


  const value = useMemo(() => ({
    token,
    user,
    role: user?.role || null,
    isAuthenticated: !!token && !!user,
    loading,
    error,
    login,
    signup,
    requestPasswordReset,
    resetPassword,
    changePassword,
    deleteAccount,
    logout,
  }), [token, user, loading, error, login, signup, requestPasswordReset, resetPassword, changePassword, deleteAccount, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const hasRole = (role, ...allowed) => {
  if (!role) return false;
  if (role === 'superuser') return true;
  return allowed.includes(role);
};
